import React, { useCallback, useMemo, useState } from 'react';

import { Confirm, Icons, Modal, Table } from '@components/common';

import { useMutateCreateAssetRequestDocument } from '@hooks/assets/useMutateCreateAssetRequestDocument';
import { useMutateDeleteAssetRequestDocument } from '@hooks/assets/useMutateDeleteAssetRequestDocument';
import { useMutateUpdateAssetRequestDocument } from '@hooks/assets/useMutateUpdateAssetRequestDocument';
import { useQueryAssetRequestDocuments } from '@hooks/assets/useQueryAssetRequestDocuments';
import { useDownloadFile } from '@hooks/common/useDownloadFile';

import { IAssetFile } from '@interfaces/assets/asset';
import { ISortBy, ITableSort } from '@interfaces/components/table';
import { IAssetRentRequestDocument } from '@interfaces/rent/assetRent';

import {
	addDocumentText,
	addDocumentTooltip,
	deleteModalQuestion,
	descriptionModalTitle,
	emptyText,
	MAX_DOCUMENTS_COUNT,
	modalWidth,
} from './constants';

import AttachRequestDocuments from './AttachRequestDocuments';
import { getColumns } from './columns';
import DescriptionsModal from './DescriptionsModal';
import UploadContractDocuments from './UploadContractDocuments';

import styles from './AttachedContractDocuments.module.scss';

interface IAttachedContractDocumentsProps {
	requestId: string;
	isAo: boolean;
	isEditAccess: boolean;
}

const AttachedContractDocuments = ({
	requestId,
	isAo,
	isEditAccess,
}: IAttachedContractDocumentsProps) => {
	const [isDirtyForm, setIsDirtyForm] = useState(false);
	const [isAttachDocument, setIsAttachDocument] = useState(false);
	const [isDeleteModal, setIsDeleteModal] = useState(false);
	const [isDescriptionModal, setIsDescriptionModal] = useState(false);
	const [isUploadModal, setIsUploadModal] = useState(false);

	const [sortDirection, setSortDirection] = useState<boolean>(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [selectedRow, setSelectedRow] = useState<IAssetRentRequestDocument | null>(null);
	const [uploadedFile, setUploadedFile] = useState<IAssetFile | null>(null);

	const openUploadFile = useCallback(() => setIsAttachDocument(true), []);
	const closeUploadFile = useCallback(() => setIsAttachDocument(false), []);

	const openDeleteModal = useCallback(() => setIsDeleteModal(true), []);
	const closeDeleteModal = useCallback(() => {
		setSelectedRow(null);
		setIsDeleteModal(false);
	}, []);

	const openDescriptionModal = useCallback(() => setIsDescriptionModal(true), []);
	const closeDescriptionModal = useCallback(() => {
		setSelectedRow(null);
		setIsDescriptionModal(false);
	}, []);

	const openUploadModal = useCallback(() => setIsUploadModal(true), []);
	const closeUploadModal = useCallback(() => setIsUploadModal(false), []);

	const closeModals = useCallback(() => {
		closeUploadFile();
		closeUploadModal();
		setIsDirtyForm(false);
		setSelectedRow(null);
		setUploadedFile(null);
	}, [closeUploadFile, closeUploadModal]);

	const {
		requestDocuments,
		isLoadingRequestDocuments,
		count,
	} = useQueryAssetRequestDocuments({
		requestId,
		isAo,
		sortFieldName,
		sortDirection,
	});

	const isAddDocumentDisabled = requestDocuments.length >= MAX_DOCUMENTS_COUNT;
	const fileName = uploadedFile ? uploadedFile.name : selectedRow?.file?.name;

	const { createDocument, isLoadingCreateDocument } = useMutateCreateAssetRequestDocument(closeModals);
	const { updateDocument, isLoadingUpdateDocument } = useMutateUpdateAssetRequestDocument(closeModals);
	const { deleteDocument, isLoadingDeleteDocument } = useMutateDeleteAssetRequestDocument(closeDeleteModal);

	const { downloadFile } = useDownloadFile();

	const handleUploadFiles = useCallback((file: IAssetFile) => {
		openUploadModal();
		setIsDirtyForm(true);
		setUploadedFile(file);
	}, [openUploadModal]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const handleUploadDocument = useCallback((description: string): void => {
		if (uploadedFile) {
			createDocument({ requestId, fileId: uploadedFile.id, description: description || '' });
		} else if (selectedRow) {
			updateDocument({ requestId, documentId: selectedRow.id, description: description || '' });
		}

	}, [createDocument, updateDocument, requestId, selectedRow, uploadedFile]);

	const handleDeleteDocument = () => {
		if (!selectedRow) return;

		deleteDocument({ requestId, documentId: selectedRow.id });
	};

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort: handleSort,
	}), [handleSort, sortDirection, sortFieldName]);

	const columns = useMemo(() => {
		return getColumns({
			setSelectedRow,
			openDeleteModal,
			openDescriptionModal,
			downloadFile,
			openUploadModal,
			isEditAccess,
			isAo,
		});
	}, [
		downloadFile,
		isAo,
		isEditAccess,
		openDeleteModal,
		openDescriptionModal,
		openUploadModal,
		setSelectedRow,
	]);

	return (
		<div className={styles.container}>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyForm}
				isOpen={isAttachDocument}
				onClose={closeUploadFile}
				title='Upload file'
				width={modalWidth}
			>
				<AttachRequestDocuments
					handleUploadFiles={handleUploadFiles}
					onClose={closeUploadFile}
					requestId={requestId}
					setIsDirtyForm={setIsDirtyForm}
					toggleUploadFilesModal={closeUploadFile}
				/>
			</Modal>
			<Modal
				isOpen={isDeleteModal}
				onClose={closeDeleteModal}
			>
				<Confirm
					isLoading={isLoadingDeleteDocument}
					onClose={closeDeleteModal}
					onSubmit={handleDeleteDocument}
					question={deleteModalQuestion}
				/>
			</Modal>
			{selectedRow && (
				<Modal
					isOpen={isDescriptionModal}
					onClose={closeDescriptionModal}
					title={descriptionModalTitle}
					width={modalWidth}
				>
					<DescriptionsModal
						closeModal={closeDescriptionModal}
						value={selectedRow?.description}
					/>
				</Modal>

			)}
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyForm}
				isOpen={isUploadModal}
				onClose={closeModals}
				title='Upload file'
				width={modalWidth}
			>
				<UploadContractDocuments
					description={selectedRow?.description || ''}
					fileName={fileName as string}
					isLoading={isLoadingCreateDocument || isLoadingUpdateDocument}
					onClose={closeModals}
					onSubmit={handleUploadDocument}
					setIsDirtyForm={setIsDirtyForm}
				/>
			</Modal>

			<Table
				addIcon={Icons.import}
				addText={`${addDocumentText} ${count}/${MAX_DOCUMENTS_COUNT}`}
				addTooltipText={addDocumentTooltip}
				columns={columns}
				data={requestDocuments}
				emptyText={emptyText}
				isAddDisabled={isAddDocumentDisabled}
				isCreatable={isEditAccess}
				isEditAccess
				isEmptyTextCenter
				isLoading={isLoadingRequestDocuments}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
				sort={sort}
				toggleAddModal={openUploadFile}
			/>
		</div>
	);
};

export default AttachedContractDocuments;
